// The two dialogs a scheduled change is decided in — t-159 §3 (confirming the
// package) and §6 (an immediate save colliding with one that is waiting).

import { AlertTriangle, Clock } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { cutEvent, formatMoment, type NextCut } from "./pendingWhen";
import { DiffBox } from "./pendingDiffView";
import type { DiffGroup } from "./pendingDiff";
import { wasCreateOnly } from "./pendingFields";
import styles from "./pending.module.css";

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// --- §3 Confirming the package ---------------------------------------------

export interface SchedulePackageModalProps {
  groups: DiffGroup[];
  /** When the package will be applied, where the host knows the date. */
  nextCut?: NextCut;
  onConfirm: () => void;
  onClose: () => void;
  saving?: boolean;
}

export function SchedulePackageModal({
  groups,
  nextCut,
  onConfirm,
  onClose,
  saving,
}: SchedulePackageModalProps) {
  const rows = groups.flatMap((g) => g.rows);
  const count = rows.length;
  const reopened = rows.filter((r) => wasCreateOnly(r.field)).length;
  const at = formatMoment(nextCut?.at);
  const event = cutEvent(nextCut);

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title={`Schedule ${count} ${plural(count, "change", "changes")} for the next cycle?`}
      icon={<Clock size={17} aria-hidden />}
      closeDisabled={saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={saving}>
            Schedule
          </Button>
        </>
      }
    >
      <p className={styles.bodyText}>
        The board keeps running exactly as it is until {event}. All {count}{" "}
        land together, in the moment{" "}
        {nextCut?.kind === "opens" ? "it opens" : "the next cycle is cut"}
        {reopened > 0 && (
          <>
            {" "}
            — including the {reopened}{" "}
            {plural(reopened, "one that used to be", "that used to be")} settable
            only when the board was created
          </>
        )}
        .
      </p>

      <DiffBox groups={groups} variant="modal" />

      <div className={styles.appliesRow}>
        <span className={styles.appliesLabel}>Applies</span>
        <div className={styles.appliesValue}>
          {nextCut?.kind === "opens"
            ? at
              ? `${at} — when the next cycle opens`
              : "When the next cycle opens"
            : at
              ? `${at} — when the open cycle closes and the next one is cut`
              : "When the open cycle closes and the next one is cut"}
        </div>
        <span className={styles.helper}>
          Re-validated at that moment against the board as it stands then. If
          they no longer fit, <b>nothing</b> is applied, the next cycle{" "}
          {nextCut?.kind === "opens" ? "opens" : "is cut"} unchanged, and this
          page tells you why.
        </span>
      </div>
    </Modal>
  );
}

// --- §6 Saving by hand over a scheduled value -------------------------------

export interface SaveNowConflictModalProps {
  /** The rows the immediate save takes off the package. */
  groups: DiffGroup[];
  /** How many changes stay in the package afterwards. */
  remaining: number;
  /** When those remaining changes still apply. */
  nextCut?: NextCut;
  /** What the save does that none of the fields it names says — the realtime
     reset, which travels as no key at all. */
  consequence?: string;
  onConfirm: () => void;
  onClose: () => void;
  saving?: boolean;
}

export function SaveNowConflictModal({
  groups,
  remaining,
  nextCut,
  consequence,
  onConfirm,
  onClose,
  saving,
}: SaveNowConflictModalProps) {
  const rows = groups.flatMap((g) => g.rows);
  const count = rows.length;
  const at = formatMoment(nextCut?.at);
  const event = cutEvent(nextCut);
  // One group, one heading: these rows are not a section of the form any more,
  // they are what the save is about to throw away.
  const dropped: DiffGroup[] = [
    { section: groups[0]?.section ?? "Identity", rows },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title={`Save now — this drops ${count} scheduled ${plural(count, "change", "changes")}`}
      icon={<AlertTriangle size={17} aria-hidden />}
      iconTone="danger"
      closeDisabled={saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={saving}>
            Save now
          </Button>
        </>
      }
    >
      <p className={styles.bodyText}>
        You are setting{" "}
        {rows.map((r, i) => (
          <span key={r.api}>
            {i > 0 && (i === rows.length - 1 ? " and " : ", ")}
            <code>{r.api}</code>
          </span>
        ))}{" "}
        by hand, right now. The {plural(count, "value", "values")} scheduled for
        the same {plural(count, "field is", "fields are")} dropped: what you set
        by hand is the last word on that field.
        {consequence && ` ${consequence}`}
      </p>

      <DiffBox
        groups={dropped}
        variant="muted"
        counts={false}
        groupLabel="Dropped from the package"
      />

      {remaining > 0 && (
        <span className={styles.helperBlock}>
          The other {remaining} {plural(remaining, "change is", "changes are")}{" "}
          untouched and still {plural(remaining, "applies", "apply")} when{" "}
          {event}{at ? ` on ${at}` : ""}.
        </span>
      )}
    </Modal>
  );
}
